import Link from "next/link";
import { brand } from "@config/brand";

type BrandLogoProps = {
  /** Pass `null` to render without a link (e.g. hero). */
  href?: string | null;
  /** Show wordmark next to the mark (header default). */
  withWordmark?: boolean;
  /** Larger mark for hero / marketing. */
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Invert wordmark for dark / photo backgrounds. */
  invert?: boolean;
};

const MARK = {
  sm: 32,
  md: 40,
  lg: 72,
} as const;

export function BrandLogo({
  href = "/",
  withWordmark = true,
  size = "sm",
  className = "",
  invert = false,
}: BrandLogoProps) {
  const px = MARK[size];
  const word =
    size === "lg"
      ? "text-5xl leading-none md:text-7xl"
      : size === "md"
        ? "text-2xl"
        : "text-xl";

  const inner = (
    <span
      className={`inline-flex items-center gap-2.5 ${className}`.trim()}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/logo-mark.svg"
        alt=""
        width={px}
        height={px}
        className="shrink-0"
        decoding="async"
      />
      {withWordmark ? (
        <span
          className={`font-[family-name:var(--font-display)] ${word} tracking-tight ${
            invert ? "text-white" : "text-[var(--ink)]"
          }`}
        >
          {brand.businessName}
        </span>
      ) : (
        <span className="sr-only">{brand.businessName}</span>
      )}
    </span>
  );

  if (href == null) return inner;

  return (
    <Link href={href} className="inline-flex items-center" aria-label={brand.businessName}>
      {inner}
    </Link>
  );
}
